import { useState } from 'react'
import { ErrorBanner, ListState, useAsync } from '../../components/Async'
import { PageTitle } from '../../components/PageTitle'
import { emptyStateFor, type EmptyStateCopy, type EmptyStateKind } from '../../lib/emptyState'
import { people, type PersonExposure } from '../../lib/api'

const COPY: Record<Exclude<EmptyStateKind, 'rows'>, EmptyStateCopy> = {
  loading: { message: 'Working out who is exposed…' },
  error: {
    message: 'Could not work out exposure.',
    hint: 'The fleet is still reporting — this is the console, not the agents.',
    action: 'Retry'
  },
  'first-run': {
    message: 'Nobody is exposed yet.',
    hint: 'This ranks people by what left for a personal AI account. It fills in as the fleet reports.'
  },
  filtered: { message: 'Nobody is exposed in this window.', action: 'Widen the window' },
  // The honest outcome, and the one worth writing copy for: almost nobody should be on this list.
  'good-news': {
    message: 'Nobody is exposed in this window.',
    hint: 'Every person the fleet saw scored zero. That is the shape this screen is supposed to have.'
  }
}

const BANDS: Record<PersonExposure['exposureBand'], { label: string; tone: 'danger' | 'accent' | 'muted' }> = {
  severe: { label: 'Severe', tone: 'danger' },
  elevated: { label: 'Elevated', tone: 'danger' },
  watch: { label: 'Watch', tone: 'accent' },
  low: { label: 'Low', tone: 'muted' }
}

/**
 * Exposure by person rather than by rule.
 *
 * Every other screen is organized by rule, by device or by case, which is the right unit for triage
 * and the wrong one for the conversation a director actually starts: not "which pattern matched" but
 * "who is sending company data out". Nothing in this product answered that.
 *
 * Two things about it are deliberate and are the reason it can exist at all.
 *
 * **The score shows its working.** An unexplained number against a named employee is a liability - if
 * nobody can say why somebody is Elevated, it gets ignored or misused - so the formula is published,
 * fetched from the server rather than restated here, and printed under the table. Every input is
 * something the person could see and contest: the severity of their own detections, how much of their
 * own output went to a personal account, how many assistants, and how many times they did it again
 * after somebody spoke to them. Nothing about department, role or tenure.
 *
 * **Two identities, and the honest one is the key.** `principalUser` is the account signed in to the
 * machine and is on every event. The assistant account is the one a director means, and it only
 * exists where an account read resolved - so it is shown beside the key rather than instead of it,
 * and its absence is not reported as "no account" (ADR-S-040).
 *
 * Rows reflow rather than sitting in a rigid grid, so the screen needs no separate phone layout.
 */
export function PeoplePage() {
  const [days, setDays] = useState(30)
  const list = useAsync(() => people.list(days), [days])
  const formula = useAsync(() => people.formula(), [])

  const kind = emptyStateFor({
    value: list.value?.items ?? null,
    error: list.error,
    loading: list.loading,
    filtered: false,
    // "Nobody scored" is good news only once we know the fleet was actually seen. Before that it is a
    // first run, which offers a next step instead of congratulating somebody on an empty fleet.
    everHadRows: (list.value?.peopleSeen ?? 0) > 0
  })

  return (
    <>
      <div className="page-header">
        <div>
          <PageTitle>People</PageTitle>
          <p>
            Exposure by person rather than by rule — the question a director asks is who is sending
            company data out, not which pattern matched.
          </p>
        </div>
        <select
          aria-label="Window"
          value={days}
          onChange={(event) => setDays(Number(event.target.value))}
          data-testid="people-window"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      <ErrorBanner message={list.error} />

      {kind === 'rows' && list.value && (
        <>
          <p className="muted" data-testid="people-count">
            {list.value.items.length} of {list.value.peopleSeen}{' '}
            {list.value.peopleSeen === 1 ? 'person' : 'people'} the fleet saw has any exposure at all.
          </p>

          <div className="people" data-testid="people-list">
            {list.value.items.map((person) => {
              const band = BANDS[person.exposureBand]

              return (
                <div className="person" key={person.principalUser}>
                  <div className="person-who">
                    <span className="person-name">{person.principalUser}</span>
                    <span className="muted">
                      {/*
                        The assistant account where one resolved. Never a placeholder when it did not:
                        "nobody could read the account row" is not the same as "they had no account",
                        and inventing the difference here is the one way this screen could mislead.
                      */}
                      {person.assistantAccount ?? 'assistant account not read'} ·{' '}
                      {person.deviceCount} {person.deviceCount === 1 ? 'device' : 'devices'}
                    </span>
                  </div>

                  <div className="person-exposure">
                    <span className="exposure-line">
                      <span className="eyebrow">Exposure</span>
                      <span className={`exposure-band ${band.tone}`} title={`Score ${person.exposureScore} of 100`}>
                        {band.label}
                      </span>
                    </span>
                    <span className="exposure-track" aria-hidden="true">
                      <span className={`exposure-fill ${band.tone}`} style={{ width: `${person.exposureScore}%` }} />
                    </span>
                  </div>

                  <div className="person-stats">
                    <span>
                      <span className="eyebrow">Detections</span>
                      <span className="tabular">{person.detections.toLocaleString()}</span>
                    </span>
                    <span>
                      <span className="eyebrow">To a personal account</span>
                      <span className="tabular">{person.personalAccountEvents.toLocaleString()}</span>
                    </span>
                    <span>
                      <span className="eyebrow">Mostly to</span>
                      <span>{person.topDestination ?? 'could not be attributed'}</span>
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        </>
      )}

      <ListState
        kind={kind}
        copy={COPY}
        columns={4}
        onAction={() => (list.error ? list.reload() : setDays(90))}
      />

      {/*
        The formula, under the table rather than in a tooltip nobody opens.

        Fetched rather than restated, because a second copy in the browser is exactly how the
        explanation comes to disagree with the arithmetic - and an explanation that is wrong is worse
        than none, because somebody will believe it.
      */}
      {formula.value && (
        <details className="card" data-testid="exposure-formula">
          <summary className="card-body">How exposure is worked out (version {formula.value.version})</summary>
          <div className="card-body">
            <p className="muted" style={{ marginTop: 0 }}>
              A score out of a hundred, from four things a person can see and contest. Nothing about
              department, role or tenure contributes, and no single measure reaches the top band alone.
            </p>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Measure</th>
                    <th>Weight</th>
                    <th>What it counts</th>
                    <th className="secondary">As bad as it gets at</th>
                  </tr>
                </thead>
                <tbody>
                  {formula.value.terms.map((term) => (
                    <tr key={term.name}>
                      <td>{term.name}</td>
                      <td className="tabular">{Math.round(term.weight * 100)}%</td>
                      <td className="muted">{term.input}</td>
                      <td className="muted secondary">{term.denominator}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="hint">
              Bands:{' '}
              {formula.value.bands
                .map((entry) => `${entry.name} ${entry.from}–${entry.to}`)
                .join(' · ')}
              . The version is stored with every score, so a change to these weights is visible rather
              than silently restating history.
            </p>
          </div>
        </details>
      )}
    </>
  )
}
